// src/pages/CommunitiesPlainBootstrap.tsx
import { useState, useEffect, useMemo, useRef } from "react";
import { Header } from "homepage/homeHeader";
import Footer from "components/footer";
import api from "services/api";
import debounce from "lodash/debounce";
import Select from "react-select";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showNoPartners, setShowNoPartners] = useState(false);
  const [selectedService, setSelectedService] = useState([]);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedRadius] = useState(20);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const performSearch = async (value, services, categories, cities, partnerId = null) => {
    try {
      const params = new URLSearchParams();
      params.append("status", "Active");
      if (partnerId) {
        params.append("partner_id", partnerId);
      } else if (value) {
        params.append("search", value);
      }
      if (services && services.length > 0) {
        services.forEach((ind) => {
          params.append("industry_id[]", ind.value);
        });
      }
      if (categories && categories.length > 0) {
        categories.forEach((cat) => {
          params.append("category_id[]", cat.value);
        });
      }
      if (cities && cities.length > 0) {
        cities.forEach((city) => {
          params.append("city_id[]", city.value);
        });
      }

      const response = await api.get(`/partners?${params.toString()}`);
      const partnersList = response?.data?.data?.partners || [];
      setShowNoPartners(false);
      setPartners(partnersList);
    } catch (error) {
      console.log("membershipPersonDetails error:", error);
    }
  };

  const debouncedSearch = useMemo(
    () => debounce(performSearch, 500),
    [] // re-create only if radius changes
  );

  // Fetch autosuggestions
  const fetchSuggestions = async (query, cities = []) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("q", query);
      if (cities && cities.length > 0) {
        cities.forEach((city) => {
          params.append("city_id[]", city.value);
        });
      }
      const response = await api.get(`/partners/autosuggest?${params.toString()}`);
      const suggestionsList = response?.data?.data?.suggestions || [];
      setSuggestions(suggestionsList);
      setShowSuggestions(suggestionsList.length > 0);
    } catch (error) {
      console.log("autosuggest error:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const debouncedFetchSuggestions = useMemo(
    () => debounce(fetchSuggestions, 300),
    []
  );

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    console.log("Suggestion clicked:", suggestion);
    if (suggestion.type === "partner") {
      // For partner: clear all filters and use partner_id
      setSearch(suggestion.name);
      setSelectedService([]);
      setSelectedCategories([]);
      setShowSuggestions(false);
      setSuggestions([]);
      performSearch("", [], [], selectedCities, suggestion.id);
    } else if (suggestion.type === "service") {
      // For service: clear search and other filters, only use this single service
      const newService = { value: suggestion.id, label: suggestion.name };
      setSelectedService([newService]); // Single value only
      setSelectedCategories([]); // Clear categories
      setSearch(suggestion.name); // Show selected service name in search input
      setShowSuggestions(false);
      setSuggestions([]);
      performSearch("", [newService], [], selectedCities);
    } else if (suggestion.type === "category") {
      // For category: clear search and other filters, only use this single category
      const newCategory = { value: suggestion.id, label: suggestion.name };
      setSelectedCategories([newCategory]); // Single value only
      setSelectedService([]); // Clear services
      setSearch(suggestion.name); // Show selected category name in search input
      setShowSuggestions(false);
      setSuggestions([]);
      performSearch("", [], [newCategory], selectedCities);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside suggestions dropdown or search input
      const isClickInsideSuggestions = suggestionsRef.current?.contains(event.target);
      const isClickInsideSearch = searchInputRef.current?.contains(event.target);
      const isClickOnSuggestion = event.target.closest('.suggestion-item');

      if (!isClickInsideSuggestions && !isClickInsideSearch && !isClickOnSuggestion) {
        setShowSuggestions(false);
      }
    };

    // Use click instead of mousedown to allow suggestion clicks to fire first
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Ensure Bootstrap Icons CSS is loaded
    const linkId = 'bootstrap-icons-css';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }

    fetchPartnersData();
    fetchCities();
  }, []);

  const fetchPartnersData = async () => {
    setShowNoPartners(false);
    try {
      const response = await api.get(
        `/partners?status=Active`
      );
      const partnersList = response?.data?.data?.partners || [];
      setPartners(partnersList);
    } catch (error) {
      console.log("membershipPersonDetails error:", error);
    }
  };



  const fetchCities = async () => {
    try {
      const response = await api.get(`/cities`);
      const citiesList = response?.data?.data?.cities || [];
      setCities(citiesList);
    } catch (error) {
      console.log("cities error:", error);
    }
  };

  const onNearMe = () => {
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const response = await api.get(
            `/partners?user_location[latitude]=${coords.latitude}&user_location[longitude]=${coords.longitude}&radius=${selectedRadius}&status=Active`
          );
          const foundPartners = response?.data?.data?.partners || [];
          setPartners(foundPartners);

          // If no partners found, show message
          if (foundPartners.length === 0) {
            setShowNoPartners(true);
          } else {
            setShowNoPartners(false);
          }
        } catch (error) {
          console.log("NearMe API error:", error);
        }
      },
      () => alert("Unable to get your location")
    );
  };

  // Show only selected partner details
  const handleViewPartner = (partner) => {
    setSelectedPartner(partner);
  };

  // Show all partners list again
  const handleShowList = () => {
    setSelectedPartner(null);
  };

  // Handle city change
  const handleCityChange = (selectedOptions) => {
    setSelectedCities(selectedOptions || []);
    debouncedSearch(search, selectedService, selectedCategories, selectedOptions || []);
    // Refresh suggestions if there's an active search query
    if (search && search.length >= 2) {
      debouncedFetchSuggestions(search, selectedOptions || []);
    }
  };



  // Format cities for react-select
  const cityOptions = useMemo(() => {
    return cities.map((city) => ({
      value: city.city_id,
      label: city.name,
    }));
  }, [cities]);

  return (
    <>
      <Header />
      <div className="container-fluid py-4 px-3 px-md-4">
        {/* Search and Filters Section */}
        <div className="card shadow-sm mb-4 border-0">
          <div className="card-body p-3 p-md-4">
            <div className="row g-3">
              {/* City */}
              <div className="col-12 col-md-6 col-lg-3">
                <div className="position-relative">
                  <div className="position-absolute d-flex align-items-center" style={{ left: 12, top: 0, bottom: 0, zIndex: 1000, pointerEvents: 'none', height: '42px' }}>
                    <i className="bi bi-geo-alt-fill" style={{ color: '#6c757d', fontSize: '16px', display: 'inline-block', lineHeight: '1' }}></i>
                  </div>
                  <Select
                    isMulti
                    options={cityOptions}
                    value={selectedCities}
                    onChange={handleCityChange}
                    placeholder="Search Location"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 42,
                        borderColor: "#d9dee3",
                        paddingLeft: 35,
                        "&:hover": {
                          borderColor: "#696cff",
                        },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        marginLeft: 0,
                      }),
                    }}
                  />
                </div>
              </div>

              {/* Search Input */}
              <div className="col-12 col-md-6 col-lg-9">
                <div className="position-relative">
                  <div className="input-group" ref={searchInputRef}>
                    <span className="input-group-text bg-light d-flex align-items-center justify-content-center" style={{ borderRight: 'none', minWidth: '45px', borderColor: '#d9dee3' }}>
                      <i className="bi bi-search" style={{ color: '#6c757d', fontSize: '16px', display: 'inline-block', lineHeight: '1' }}></i>
                    </span>
                    <input
                      className="form-control border-start-0"
                      type="text"
                      value={search}
                      placeholder="Search Specialities, Doctors, Clinics, Hospitals..."
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearch(value);
                        debouncedFetchSuggestions(value, selectedCities);
                        debouncedSearch(value, selectedService, selectedCategories, selectedCities);
                      }}
                      onFocus={() => {
                        if (suggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      style={{ borderColor: '#d9dee3' }}
                    />
                    {search && (
                      <button
                        className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                        type="button"
                        onClick={() => {
                          setSearch("");
                          setSuggestions([]);
                          setShowSuggestions(false);
                          debouncedSearch("", selectedService, selectedCategories, selectedCities);
                        }}
                        style={{ borderLeft: 'none', minWidth: '42px', borderColor: '#d9dee3' }}
                        aria-label="Clear search"
                      >
                        <i className="bi bi-x-lg" style={{ fontSize: '14px' }}></i>
                      </button>
                    )}
                  </div>
                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="position-absolute w-100 bg-white border rounded shadow-lg"
                      style={{
                        top: "100%",
                        left: 0,
                        zIndex: 1000,
                        maxHeight: "400px",
                        overflowY: "auto",
                        marginTop: "4px",
                        borderColor: '#d9dee3',
                      }}
                    >
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={`${suggestion.type}-${suggestion.id}-${index}`}
                          className="p-3 border-bottom cursor-pointer suggestion-item"
                          style={{
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                            borderColor: '#e9ecef',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "white";
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSuggestionClick(suggestion);
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <i
                                className={`bi me-2 ${suggestion.type === "partner"
                                  ? "bi-building text-primary"
                                  : suggestion.type === "service"
                                    ? "bi-briefcase text-info"
                                    : "bi-tags text-success"
                                  }`}
                              ></i>
                              <span className="fw-medium">{suggestion.name}</span>
                            </div>
                            <span className="badge bg-secondary-subtle text-secondary text-uppercase small">
                              {suggestion.type === "partner"
                                ? "PARTNER"
                                : suggestion.type === "service"
                                  ? "SERVICE"
                                  : "CATEGORY"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="row g-4">
          {/* Partners List/Details Section */}
          <div className="col-12">
            <div className="card shadow-sm h-100 border-0">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
                <h5 className="card-title mb-0 fw-semibold">
                  <i className="bi bi-building me-2 text-primary"></i>
                  {selectedPartner ? "Partner Details" : `Partners${partners.length > 0 ? ` (${partners.length})` : ''}`}
                </h5>
                <div className="d-flex gap-2">
                  {!selectedPartner && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={onNearMe}
                    >
                      <i className="bi bi-geo-alt-fill me-1"></i>
                      Near Me
                    </button>
                  )}
                  {selectedPartner && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleShowList}
                    >
                      <i className="bi bi-list-ul me-1"></i>
                      Show List
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body" style={{ paddingTop: "1.5rem", minHeight: "400px" }}>
                {showNoPartners ? (
                  <div className="alert alert-warning text-center border-0 shadow-sm" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
                    <i className="bi bi-exclamation-triangle-fill me-2 fs-4"></i>
                    <h6 className="mt-2 mb-2">No Partners Found Near Your Location</h6>
                    <p className="mb-3 text-muted small">We couldn't find any partners within your selected radius. Try expanding your search or check all available partners.</p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={fetchPartnersData}
                    >
                      <i className="bi bi-arrow-left me-1"></i>
                      Show All Partners
                    </button>
                  </div>
                ) : selectedPartner ? (
                  <div className="partner-details">
                    <div className="text-center mb-4">
                      {selectedPartner.logo_url && (
                        <img
                          src={selectedPartner.logo_url}
                          alt={selectedPartner.name}
                          className="img-fluid rounded shadow-sm mb-3"
                          style={{ maxHeight: 120, maxWidth: 200, objectFit: "contain" }}
                        />
                      )}
                      <h4 className="mb-2">{selectedPartner.name}</h4>
                      {selectedPartner.type && (
                        <p className="text-muted mb-3">
                          <i className="bi bi-tag me-1"></i>
                          {selectedPartner.type}
                        </p>
                      )}
                    </div>

                    <div className="row g-3">
                      {selectedPartner.city && (
                        <div className="col-12 col-md-6">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-geo-alt-fill text-primary me-2 mt-1"></i>
                            <div>
                              <strong className="d-block text-muted small">City</strong>
                              <span>{selectedPartner.city.name}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedPartner.address && (
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-house-door-fill text-primary me-2 mt-1"></i>
                            <div>
                              <strong className="d-block text-muted small">Address</strong>
                              <span>{selectedPartner.address}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedPartner.description && (
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-file-text-fill text-primary me-2 mt-1"></i>
                            <div className="flex-grow-1">
                              <strong className="d-block text-muted small mb-1">Description</strong>
                              <div
                                className="text-muted"
                                dangerouslySetInnerHTML={{
                                  __html: selectedPartner.description || "",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedPartner.industries && selectedPartner.industries.length > 0 && (
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-briefcase-fill text-primary me-2 mt-1"></i>
                            <div>
                              <strong className="d-block text-muted small mb-1">Services</strong>
                              <div>
                                {selectedPartner.industries.map((ind, idx) => (
                                  <span key={ind.id} className="badge bg-primary-subtle text-primary me-1 mb-1">
                                    {ind.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedPartner.categories && selectedPartner.categories.length > 0 && (
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-tags-fill text-primary me-2 mt-1"></i>
                            <div>
                              <strong className="d-block text-muted small mb-1">Categories</strong>
                              <div>
                                {selectedPartner.categories.map((cat, idx) => (
                                  <span key={cat.category_id} className="badge bg-success-subtle text-success me-1 mb-1">
                                    {cat.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedPartner.inclusions && (
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                            <div className="flex-grow-1">
                              <strong className="d-block text-muted small mb-1">Inclusions</strong>
                              <div
                                className="text-muted"
                                dangerouslySetInnerHTML={{
                                  __html: selectedPartner.inclusions || "",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedPartner.exclusions && (
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-x-circle-fill text-danger me-2 mt-1"></i>
                            <div className="flex-grow-1">
                              <strong className="d-block text-muted small mb-1">Exclusions</strong>
                              <div
                                className="text-muted"
                                dangerouslySetInnerHTML={{
                                  __html: selectedPartner.exclusions || "",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : partners.length > 0 ? (
                  <div className="row g-3 g-md-4">
                    {partners.map((c) => (
                      <div className="col-12 col-sm-6 col-md-4" key={c.id}>
                        <div className="card border h-100 shadow-sm hover-shadow" style={{ transition: 'all 0.3s ease', borderColor: '#e9ecef' }}>
                          <div className="card-body d-flex flex-column p-3 p-md-4">
                            <h5 className="card-title mb-2 fw-semibold" style={{ fontSize: '1.1rem', lineHeight: '1.4' }}>{c.name}</h5>
                            {c.type && (
                              <h6 className="card-subtitle mb-2 text-muted small">
                                <i className="bi bi-tag me-1"></i>
                                {c.type}
                              </h6>
                            )}
                            {c.address && (
                              <p className="card-text text-muted small mb-3 flex-grow-1" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                                <i className="bi bi-geo-alt me-1"></i>
                                {c.address}
                              </p>
                            )}
                            <button
                              className="btn btn-primary btn-sm w-100 mt-auto"
                              onClick={() => handleViewPartner(c)}
                              style={{ marginTop: 'auto' }}
                            >
                              <i className="bi bi-eye me-1"></i>
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox display-1 text-muted mb-3"></i>
                    <h5 className="text-muted mb-2">No Partners Found</h5>
                    <p className="text-muted mb-3">Try adjusting your search criteria or filters.</p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={fetchPartnersData}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Reset Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <style>{`
        .hover-shadow {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-shadow:hover {
          transform: translateY(-4px);
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12) !important;
          border-color: #696cff !important;
        }
        .react-select-container .react-select__control {
          border-radius: 0.5rem;
          transition: all 0.2s ease;
        }
        .react-select-container .react-select__control:hover {
          border-color: #696cff;
        }
        .react-select-container .react-select__control--is-focused {
          border-color: #696cff;
          box-shadow: 0 0 0 0.2rem rgba(105, 108, 255, 0.25);
        }
        .bi-geo-alt-fill,
        .bi-search {
          display: inline-block !important;
          font-style: normal !important;
          font-variant: normal !important;
          text-rendering: auto !important;
          line-height: 1 !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        .suggestion-item:last-child {
          border-bottom: none !important;
        }
        .suggestion-item:hover {
          background-color: #f8f9fa !important;
        }
        @media (max-width: 768px) {
          .card-body {
            padding: 1rem !important;
          }
          .input-group-text {
            min-width: 40px !important;
          }
        }
        @media (max-width: 576px) {
          .container-fluid {
            padding-left: 0.75rem !important;
            padding-right: 0.75rem !important;
          }
        }
      `}</style>
    </>
  );
}
